import { useEffect } from 'react';
import { useLauncher } from '@/hooks/useLauncher';
import { useOrganizations } from '@/hooks/useOrganizations';
import { Flex, Modal, ModalContent, ModalOverlay } from '@/ui';
import { Sidebar } from './components/Sidebar';
import { isSettingsModalTab, useSettingsModal } from './store';
import { Account } from './tabs/Account';
import { Calendars } from './tabs/Calendars';
import { CreateOrganization } from './tabs/CreateOrganization';
import { Help } from './tabs/Help';
import { Integrations } from './tabs/Integrations';
import { Organizations } from './tabs/Organizations';
import { Subscription } from './tabs/Subscription';

export const SettingsModal = () => {
  const { isOpen, open, close, changeTab, tab, organizationId } =
    useSettingsModal();
  const { launch, clearLaunch } = useLauncher();
  const { sharedOrganizations, isLoading: organizationsLoading } =
    useOrganizations();
  // Org tabs list every shared org the user belongs to, independent of the
  // active library context.
  const showOrganizations = sharedOrganizations.length > 0;

  useEffect(() => {
    if (launch?.startsWith('settings:')) {
      const nextTab = launch.substring('settings:'.length);
      // Keep the organizations deep link behind org membership, matching nav.
      if (isSettingsModalTab(nextTab)) {
        // Wait for the profile (which carries the orgs) to resolve before
        // acting on an organizations deep link, so it isn't dropped while it
        // is still loading. The effect re-runs once it resolves.
        if (nextTab === 'organizations' && organizationsLoading) {
          return;
        }
        if (nextTab === 'organizations') {
          if (showOrganizations) {
            // The deep link doesn't name an org — default to the first one.
            open(nextTab, sharedOrganizations[0].id);
          }
        } else {
          open(nextTab);
        }
        clearLaunch();
      }
    }
  }, [
    launch,
    clearLaunch,
    open,
    showOrganizations,
    organizationsLoading,
    sharedOrganizations,
  ]);

  // Self-heal the org tab once orgs are known: bounce to the Personal account
  // (the top of the accounts list) when there are no orgs to show (left the
  // last org), and retarget to the first org when the selected one is gone
  // (deleted, removed mid-session) or unset (legacy changeTab('organizations')
  // caller).
  useEffect(() => {
    if (!isOpen || organizationsLoading) {
      return;
    }
    if (tab !== 'organizations') {
      return;
    }
    if (!showOrganizations) {
      changeTab('subscription');
    } else if (!sharedOrganizations.some((org) => org.id === organizationId)) {
      changeTab('organizations', sharedOrganizations[0].id);
    }
  }, [
    isOpen,
    tab,
    organizationId,
    organizationsLoading,
    showOrganizations,
    sharedOrganizations,
    changeTab,
  ]);

  return (
    <Modal
      isCentered
      isOpen={isOpen}
      returnFocusOnClose={false}
      variant="primary"
      onClose={close}
    >
      <ModalOverlay />
      <ModalContent
        display="flex"
        flexDir="row"
        h="calc(var(--app-height) - 50px)"
        maxH={{ md: '740px' }}
        minH={{ base: 'var(--app-height)', md: 'auto' }}
        minW={{ base: '100vw', md: '90%', lg: '1000px' }}
        overflow="hidden" // 👇 allows rounded corners with a scrollbar
      >
        <Sidebar display={{ base: 'none', md: 'flex' }} />

        <Flex direction="column" flex="1">
          {tab === 'account' ? (
            <Account />
          ) : tab === 'subscription' ? (
            <Subscription />
          ) : tab === 'help' ? (
            <Help />
          ) : tab === 'calendars' ? (
            <Calendars />
          ) : tab === 'integrations' ? (
            <Integrations />
          ) : tab === 'organizations' && showOrganizations ? (
            <Organizations />
          ) : tab === 'createOrganization' ? (
            <CreateOrganization />
          ) : null}
        </Flex>
      </ModalContent>
    </Modal>
  );
};
